// Global IST (Asia/Kolkata) timezone + canonical date/time format enforcer.
//
// Guarantees for the ENTIRE frontend, regardless of which page / component
// / library formatted the date:
//   • Every rendered date  → DD/MM/YYYY        (e.g. 17/02/2026)
//   • Every rendered time  → HH:MM AM/PM       (e.g. 10:30 AM)
//   • Every date+time      → DD/MM/YYYY, HH:MM AM/PM
//   • All computed in the Asia/Kolkata (GMT+05:30) wall clock
//
// Achieved by patching:
//   • Date.prototype.toLocaleString / toLocaleDateString / toLocaleTimeString
//   • Intl.DateTimeFormat (constructor + .format + .formatToParts)
//
// `Number.prototype.toLocaleString` is intentionally UNTOUCHED so that
// currency / count formatting continues to work.
//
// Import this file ONCE, as the very FIRST import in `index.js`.

const DEFAULT_TZ = "Asia/Kolkata";

const OrigDTF = Intl.DateTimeFormat;

// -------- Detection helpers --------

const TIME_KEYS = ["hour", "minute", "second", "timeStyle", "dayPeriod"];
const DATE_KEYS = ["day", "month", "year", "weekday", "era", "dateStyle"];

const hasAny = (obj, keys) =>
  !!obj && typeof obj === "object" && keys.some((k) => obj[k] !== undefined && obj[k] !== null);

const wantDatePart = (options) => {
  if (!options || typeof options !== "object") return true; // native default: date+time
  const hasDate = hasAny(options, DATE_KEYS);
  const hasTime = hasAny(options, TIME_KEYS);
  if (hasTime && !hasDate) return false; // caller asked ONLY for time
  return true; // includes date, or no hints (native default)
};

const wantTimePart = (options) => {
  if (!options || typeof options !== "object") return true; // native default: date+time
  const hasDate = hasAny(options, DATE_KEYS);
  const hasTime = hasAny(options, TIME_KEYS);
  if (hasDate && !hasTime) return false; // caller asked ONLY for date
  return true; // includes time, or no hints (native default)
};

// -------- Core formatters (Asia/Kolkata, canonical strings) --------

const _dateFormatter = new OrigDTF("en-GB", {
  timeZone: DEFAULT_TZ,
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
});

const _timeFormatter = new OrigDTF("en-US", {
  timeZone: DEFAULT_TZ,
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hour12: true,
});

const _formatDateISTDDMMYYYY = (date) => {
  const parts = _dateFormatter.formatToParts(date);
  const p = Object.fromEntries(parts.map((x) => [x.type, x.value]));
  // v109.9.43 — Canonical app-wide date separator is a dash (DD-MM-YYYY).
  return `${p.day}-${p.month}-${p.year}`;
};

const _formatTimeISTHHMM = (date) => {
  const parts = _timeFormatter.formatToParts(date);
  const p = Object.fromEntries(parts.map((x) => [x.type, x.value]));
  const period = (p.dayPeriod || "").toUpperCase().replace(/\./g, ""); // strip periods from "A.M."
  // v109.9.43 — Canonical app-wide time format is HH:MM:SS AM/PM (seconds included).
  return `${p.hour}:${p.minute}:${p.second} ${period}`;
};

const _formatBothIST = (date) =>
  `${_formatDateISTDDMMYYYY(date)}, ${_formatTimeISTHHMM(date)}`;

// -------- Patch Date.prototype.toLocale* --------

Date.prototype.toLocaleString = function (_locales, options) {
  if (isNaN(this)) return "Invalid Date";
  const d = wantDatePart(options);
  const t = wantTimePart(options);
  if (d && t) return _formatBothIST(this);
  if (t) return _formatTimeISTHHMM(this);
  return _formatDateISTDDMMYYYY(this);
};

Date.prototype.toLocaleDateString = function () {
  if (isNaN(this)) return "Invalid Date";
  return _formatDateISTDDMMYYYY(this);
};

Date.prototype.toLocaleTimeString = function () {
  if (isNaN(this)) return "Invalid Date";
  return _formatTimeISTHHMM(this);
};

// -------- Patch Intl.DateTimeFormat --------
// Some code uses `new Intl.DateTimeFormat(locale, options).format(d)` directly.
// We wrap the constructor so the `.format()` return value matches our canonical
// output, while `.formatToParts()` keeps returning parts (in IST) so callers
// that build their own strings still work.

function PatchedDateTimeFormat(locales, options) {
  const opts =
    options && typeof options === "object" ? { ...options } : {};
  if (!opts.timeZone) opts.timeZone = DEFAULT_TZ;

  const wantD = wantDatePart(options);
  const wantT = wantTimePart(options);

  const inner = new OrigDTF(locales, opts);

  // Return a fresh object (do NOT rely on prototype-chain assignment; the real
  // Intl.DateTimeFormat has `.format` defined as a prototype getter which
  // can't be shadowed via plain `this.format = …`).
  const canonicalFormat = (date) => {
    const d = date instanceof Date ? date : new Date(date);
    if (isNaN(d)) return "Invalid Date";
    if (wantD && wantT) return _formatBothIST(d);
    if (wantT) return _formatTimeISTHHMM(d);
    return _formatDateISTDDMMYYYY(d);
  };

  return {
    format: canonicalFormat,
    formatToParts: (date) => inner.formatToParts(date),
    formatRange: inner.formatRange ? inner.formatRange.bind(inner) : undefined,
    formatRangeToParts: inner.formatRangeToParts
      ? inner.formatRangeToParts.bind(inner)
      : undefined,
    resolvedOptions: () => ({ ...inner.resolvedOptions(), timeZone: DEFAULT_TZ }),
  };
}

PatchedDateTimeFormat.supportedLocalesOf =
  OrigDTF.supportedLocalesOf.bind(OrigDTF);

Intl.DateTimeFormat = PatchedDateTimeFormat;

// -------- Patch Date constructor to treat naive ISO strings as UTC --------
// Mongo/BSON strips tzinfo so the backend sometimes emits strings like
// "2026-07-04T14:44:43.568000" (no `Z`, no `±HH:MM`). ES spec says naive
// datetime strings are parsed as LOCAL time — which is wrong: the value
// is UTC but was serialised without its offset. We normalise here so every
// downstream `Date` is correct regardless of the browser's local timezone.
//
// Matches:
//   YYYY-MM-DDTHH:MM(:SS(.ffffff)?)?           (no Z, no offset) → append Z
// Leaves alone:
//   YYYY-MM-DD                                 (date-only, spec parses as UTC)
//   YYYY-MM-DDTHH:MM:SSZ                       (already UTC)
//   YYYY-MM-DDTHH:MM:SS+05:30                  (already zoned)
//   any non-ISO string                         (RFC 2822 etc.)

const NAIVE_ISO_RE =
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/;

const _OrigDate = Date;
function PatchedDate(...args) {
  if (args.length === 1 && typeof args[0] === "string") {
    let s = args[0];
    if (NAIVE_ISO_RE.test(s)) {
      s = s + "Z";
    }
    return new _OrigDate(s);
  }
  if (args.length === 0) return new _OrigDate();
  return new _OrigDate(...args);
}
PatchedDate.prototype = _OrigDate.prototype;
PatchedDate.now = _OrigDate.now.bind(_OrigDate);
PatchedDate.parse = (s) => {
  if (typeof s === "string" && NAIVE_ISO_RE.test(s)) s = s + "Z";
  return _OrigDate.parse(s);
};
PatchedDate.UTC = _OrigDate.UTC.bind(_OrigDate);
Date = PatchedDate;

// -------- Public constant --------

export const APP_TIMEZONE = DEFAULT_TZ;
